import { useLocation, useNavigate } from "react-router-dom";

const normalizePathname = (pathname: string) =>
  pathname.replace(/\/+$/, "") || "/";

/**
 * Navigate between the views of an agentic-logic surface.
 *
 * An embedded surface's views are query params on the HOST's own URL, so every
 * move between them — opening the builder, switching rules, exiting back to the
 * host — is a navigation to the pathname already on screen. A bare
 * `navigate(path)` clears `location.state` on such a move, and a host that
 * bootstrapped itself from that state (a personalization created from a scratch
 * card carries `isUpsell`, `showCheckoutBlocksDialog`, …) then sees its state
 * change out from under it and can re-initialize a draft the merchant is in the
 * middle of editing.
 *
 * So state rides along whenever the target is the same route we are already on,
 * and is dropped when the target is a genuinely different route — where the
 * previous page's state means nothing.
 */
export const useSurfaceNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  return (to: string) => {
    const [pathname] = to.split("?");
    const staysOnRoute =
      normalizePathname(pathname) === normalizePathname(location.pathname);
    void navigate(to, staysOnRoute ? { state: location.state } : undefined);
  };
};
